import React, { useState, useEffect, useContext } from "react";
import CustomInput from "./common/CustomInput";
import CustomSelect from "./common/CustomSelect";
import CustomHeading from "./common/CustomHeading";
import Button from "./common/Button";
import { CheckCircle } from "lucide-react";
import { getCreditLoanApplicationData, getProductJunctionDetailsByLoanAmountAndTenure, saveLoanSummaryAPI } from "../ActionCreator";
import { ReadOnlyContext } from "./ReadOnlyContext";

// ── Shared inline design tokens ───────────────────────────────────────────────
const S = {
  card: {
    background   : "rgba(255, 255, 255, 0.95)",
    border       : "1px solid #cbd5e1",
    borderRadius : 16,
    padding      : "20px",
    boxShadow    : "0 4px 20px -2px rgba(15, 23, 42, 0.04), 0 2px 8px -1px rgba(15, 23, 42, 0.02)",
  },
  sectionLabel: {
    fontSize    : 12,
    fontWeight  : 700,
    color       : "#475569",
    letterSpacing: ".06em",
    textTransform: "uppercase",
    margin      : "0 0 14px",
    paddingBottom: 8,
    borderBottom: "1.5px solid #cbd5e1",
  },
  grid3: {
    display             : "grid",
    gridTemplateColumns : "repeat(3, 1fr)",
    gap                 : 16,
  },
  grid2: {
    display             : "grid",
    gridTemplateColumns : "repeat(2, 1fr)",
    gap                 : 16,
  },
  label: {
    display     : "block",
    marginBottom: 5,
    fontWeight  : 600,
    fontSize    : 12,
    color       : "#334155",
    letterSpacing: ".01em",
  },
  textarea: {
    width       : "100%",
    border      : "1.5px solid #cbd5e1",
    borderRadius: 10,
    padding     : "10px 12px",
    minHeight   : 110,
    fontSize    : 13,
    color       : "#0f172a",
    fontFamily  : "inherit",
    lineHeight  : 1.6,
    resize      : "vertical",
    outline     : "none",
    transition  : "all 0.15s ease",
  },
};

const FirstSection = ({ allDetail, initialData, loanAppData, showPopup, onRefresh, onProductChange, admin, setLoader }) => {
  const isReadOnly = useContext(ReadOnlyContext);
  console.log("admin", admin);
  const isProductDisabled = admin?.emailId !== "SuperUser" && admin?.department !== "OPERATIONS" && admin?.department !== "CREDIT";

  const [form, setForm] = useState({
    applicantName: "",
    loanAmount: "",
    tenure: "",
    product: "",
    interestRate: "",
    expectedEmi: "",
    summary: "",
  });

  const [productOptions, setProductOptions] = useState([
    // { label: "Rs 50000 to 100000", value: "Rs 50000 to 100000" },
    // { label: "Rs 100000 to 300000", value: "Rs 100000 to 300000" },
    // { label: "Topup Upto 150000", value: "Topup Upto 150000" },
    // { label: "Rs 300000 to 500000", value: "Rs 300000 to 500000" },
  ]);
  const [productsList, setProductsList] = useState([]);

  const [btnLoading, setBtnLoading] = useState(false);

  // Load / Sync form data using getCreditLoanApplicationData API and fallbacks
  useEffect(() => {
    console.log("allDetail", allDetail)
    const activeId = allDetail?.loanId || allDetail?.userId;
    if (activeId) {
      setLoader(true)
      getCreditLoanApplicationData(activeId, (callback) => {
        setLoader(false)
        if (callback) {
          const apiData = callback?.data || callback;
          setForm((prev) => ({
            ...prev,
            applicantName: apiData?.applicantName || apiData?.tradeName || prev.applicantName || "",
            loanAmount: apiData?.loanAmount || prev.loanAmount || "",
            tenure: apiData?.tenureMonths || prev.tenure || "",
            product: (apiData?.product !== null && apiData?.product !== undefined && apiData?.product !== "") ? apiData.product : prev.product,
            // interestRate is intentionally NOT set here — it will be auto-populated
            // from the productJunction API (normalInterest) based on the selected productId
            summary: apiData?.briefLoanSummary || prev.summary || "",
            expectedEmi: apiData?.expectedEmi || prev.expectedEmi || "",
          }));
          if (onProductChange) {
            onProductChange(apiData?.product ?? "");
          }
        }
      });
    }
  }, [allDetail, onProductChange, setLoader]);

  // Sync state with parent-supplied initialData (from getAllCreditData)
  useEffect(() => {
    if (initialData || loanAppData) {
      const apiData = loanAppData?.data || loanAppData;
      setForm((prev) => {
        const nextForm = {
          ...prev,
          applicantName: initialData?.applicantName || apiData?.applicantName || apiData?.tradeName || prev.applicantName || "",
          loanAmount: initialData?.loanAmount || apiData?.loanAmount || apiData?.requestedAmount || prev.loanAmount || "",
          tenure: initialData?.tenure || apiData?.tenureMonths || prev.tenure || "",
          product: (apiData?.product !== null && apiData?.product !== undefined && apiData?.product !== "") ? apiData.product : (initialData?.product || prev.product || ""),
          interestRate: initialData?.interestRate || apiData?.interestRate || prev.interestRate || "",
          summary: initialData?.summary || apiData?.briefLoanSummary || prev.summary || "",
          expectedEmi: initialData?.expectedEmi || apiData?.expectedEmi || prev.expectedEmi || "",
        };
        if (onProductChange) {
          onProductChange(nextForm.product);
        }
        return nextForm;
      });
    }
  }, [initialData, onProductChange, loanAppData]);

  // Pre-fill from loan application API data as fallback when initialData fields are empty
  useEffect(() => {
    if (!loanAppData) return;
    const apiData = loanAppData?.data || loanAppData;
    setForm((prev) => ({
      ...prev,
      applicantName: prev.applicantName || apiData?.applicantName || apiData?.tradeName || "",
      loanAmount   : prev.loanAmount    || apiData?.loanAmount || apiData?.requestedAmount || "",
      tenure       : prev.tenure        || apiData?.tenureMonths || "",
      interestRate : prev.interestRate   || apiData?.interestRate || "",
      summary      : prev.summary       || apiData?.briefLoanSummary || "",
    }));
  }, [loanAppData]);

  // Fetch product options dynamically
  useEffect(() => {
    const lType = allDetail?.loanType || "BL";
    const lAmount = form?.loanAmount || "";
    const lTenure = form?.tenure;

    if (lTenure) {
      setLoader(true)
      getProductJunctionDetailsByLoanAmountAndTenure(lType, lAmount, lTenure, response => {
        setLoader(false)
        const rawData = response?.data || {};
        const options = [];
        const seen = new Set();
        const itemsList = [];

        const processItem = (item) => {
          const prodName = item?.productName || "";
          const prodId = item?.productId || "";
          if (prodId && !seen.has(prodId)) {
            seen.add(prodId);
            options.push({ label: prodName ?? "", value: prodId });
            itemsList.push(item);
          }
        };

        if (Array.isArray(rawData)) {
          rawData.forEach(processItem);
        } else if (typeof rawData === "object" && rawData !== null) {
          Object.keys(rawData).forEach(nbfcKey => {
            const list = rawData[nbfcKey];
            if (Array.isArray(list)) {
              list.forEach(processItem);
            }
          });
        }

        if (options.length > 0) {
          setProductOptions([{ label: "Select Product", value: "" }, ...options]);
          setProductsList(itemsList);
        }
      });
    }
  }, [allDetail?.loanType, form?.loanAmount, form?.tenure, setLoader]);

  // Auto-populate interestRate from productJunction API (normalInterest) whenever
  // the selected productId or the productsList changes. This always takes precedence
  // so the displayed rate stays in sync with the selected product.
  useEffect(() => {
    if (form.product && productsList.length > 0) {
      const selectedProd = productsList.find(
        (item) =>
          item?.productId === form.product ||
          item?.loanUtilityDO?.productId === form.product ||
          item?.productName === form.product ||
          item?.loanUtilityDO?.productName === form.product
      );
      if (selectedProd) {
        const interestVal = selectedProd?.loanUtilityDO?.normalInterest;
        if (interestVal !== undefined && interestVal !== null) {
          const displayInterest = interestVal <= 1 ? Number((interestVal * 100).toFixed(2)) : interestVal;
          setForm((prev) => ({ ...prev, interestRate: displayInterest }));
        }
      }
    }
  }, [form.product, productsList]);

  const handleChange = (e, field) => {
    const val = e.target.value;
    setForm((prev) => {
      const updated = {
        ...prev,
        [field]: val,
      };

      if (field === "product") {
        const selectedProd = productsList.find(
          (item) =>
            item?.productId === val ||
            item?.loanUtilityDO?.productId === val ||
            item?.productName === val ||
            item?.loanUtilityDO?.productName === val
        );
        if (selectedProd) {
          const interestVal = selectedProd?.loanUtilityDO?.normalInterest;
          if (interestVal !== undefined && interestVal !== null) {
            const displayInterest = interestVal <= 1 ? Number((interestVal * 100).toFixed(2)) : interestVal;
            updated.interestRate = displayInterest;
          }
        }
      }

      return updated;
    });
    if (field === "product" && onProductChange) {
      onProductChange(val);
    }
  };

  const handleSave = () => {
    const activeId = allDetail?.loanId;
    if (!activeId) {
      showPopup("No active Loan or User ID found.");
      return;
    }
    setBtnLoading(true);

    const payload = {
      applicantName: form.applicantName || "",
      loanAmount: String(form.loanAmount || ""),
      product: form.product || "",
      briefLoanSummary: form.summary || "",
      tenureMonths: form.tenure || "",
      // interestRate: form.interestRate || "",
      loanId:activeId
      // expectedEmi: form.expectedEmi || ""credit-assessment
    };
setLoader(true)
    saveLoanSummaryAPI( payload, (res) => {
      setLoader(false)
      setBtnLoading(false);
      if (res?.success === true || res?.status === 200 || res?.message === "success") {
        showPopup("Loan Particular details saved successfully");
        if (onRefresh) onRefresh();
      } else {
        showPopup(res?.message || "Error saving loan particulars");
      }
    }, (err) => {
      setBtnLoading(false);
      showPopup("Error saving loan particulars");
      console.error("Error saving loan particulars", err);
    });
  };

  return (
    <div style={{ marginTop: 12 }}>
      <CustomHeading
        title="Loan Particulars"
        subtitle="Basic information about the Loan application."
        buttonText="Positive"
        icon={CheckCircle}
        buttonColor="#4CAF50"
        borderColor="#3b82f6"
        background="#eef3f8"
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Button
          title="Save Data"
          onClick={handleSave}
          btnLoading={btnLoading}
          showInReadOnly={true}
        />
      </div>

      <div style={S.card}>
        <p style={S.sectionLabel}>Application Details</p>

        {/* Row 1 */}
        <div style={S.grid3}>
          <CustomInput
            label="Applicant Name"
            name="applicantName"
            value={form.applicantName}
            onChange={(e) => handleChange(e, "applicantName")}
            placeHolder="Enter name"
            disabled={isReadOnly}
          />
          <CustomInput
            label="Requested Amount (₹)"
            name="loanAmount"
            type="number"
            value={form.loanAmount}
            onChange={(e) => handleChange(e, "loanAmount")}
            placeHolder="0"
            disabled={isReadOnly}
          />
          <CustomInput
            label="Tenure (Months)"
            name="tenure"
            type="number"
            value={form.tenure}
            onChange={(e) => handleChange(e, "tenure")}
            placeHolder="0"
            disabled={isReadOnly}
          />
        </div>

        {/* Row 2 */}
        <div style={{ ...S.grid3, marginTop: 16 }}>
          <CustomSelect
            label="Product"
            name="product"
            value={form.product}
            onChange={(e) => handleChange(e, "product")}
            placeHolder="Select product"
            options={productOptions}
            disabled={isProductDisabled || isReadOnly}
          />
          <CustomInput
            label="Interest Rate (%)"
            name="interestRate"
            type="number"
            value={form.interestRate}
            onChange={(e) => handleChange(e, "interestRate")}
            placeHolder="0.00"
            disabled={isReadOnly}
          />
         
        </div>

        {/* Summary */}
        <div style={{ marginTop: 16 }}>
          <label style={S.label}>Brief Loan Summary</label>
          <textarea
            name="summary"
            value={form.summary}
            onChange={(e) => handleChange(e, "summary")}
            placeholder="Provide a brief summary of the loan application…"
            style={S.textarea}
            disabled={isReadOnly}
            onFocus={(e) => {
              e.target.style.borderColor = "#024e5a";
              e.target.style.boxShadow   = "0 0 0 3px rgba(2, 78, 90, 0.15)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#cbd5e1";
              e.target.style.boxShadow   = "none";
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FirstSection;
